import { getAccessToken } from './authApi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function authHeaders(): HeadersInit {
  const token = getAccessToken();
  const h: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

export interface GalleryPurchase {
  id: string;
  amount: number;
  currency: string;
  created_at: string;
  items_count?: number;
  can_download_any?: boolean;
  active_items_count?: number;
}

export interface GallerySearch {
  id: string;
  school: string;
  session_date: string;
  match_count: number;
  match_filenames: string[];
  statistics?: Record<string, unknown>;
  created_at?: string;
}

export async function updateProfile(displayName: string): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${API_URL}/users/me`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ display_name: displayName }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    return { ok: false, error: (json as { detail?: string }).detail || 'Error' };
  }
  return { ok: true };
}

const PREVIEW_AVATAR_KEY = 'surf_preview_avatar';

export function getPreviewAvatarUrl(): string | null {
  try {
    return localStorage.getItem(PREVIEW_AVATAR_KEY);
  } catch {
    return null;
  }
}

export function setPreviewAvatarUrl(dataUrl: string | null): void {
  try {
    if (dataUrl) localStorage.setItem(PREVIEW_AVATAR_KEY, dataUrl);
    else localStorage.removeItem(PREVIEW_AVATAR_KEY);
  } catch {
    /* ignore */
  }
}

export function profileAvatarUrl(cacheBust?: number): string {
  const token = getAccessToken();
  if (!token) return '';
  const q = new URLSearchParams({ access_token: token });
  if (cacheBust) q.set('t', String(cacheBust));
  return `${API_URL}/users/me/avatar?${q}`;
}

export async function uploadAvatar(file: File): Promise<{ ok: boolean; error?: string }> {
  const token = getAccessToken();
  if (!token) return { ok: false, error: 'No autenticado' };
  const fd = new FormData();
  fd.append('avatar', file, file.name);
  const res = await fetch(`${API_URL}/users/me/avatar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    return { ok: false, error: (json as { detail?: string }).detail || 'Error al subir' };
  }
  return { ok: true };
}

export async function removeAvatar(): Promise<boolean> {
  const res = await fetch(`${API_URL}/users/me/avatar`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.ok;
}

export type GalleryResult =
  | { ok: true; purchases: GalleryPurchase[]; searches: GallerySearch[] }
  | { ok: false; status: number };

export async function getGallery(): Promise<GalleryResult> {
  const res = await fetch(`${API_URL}/users/me/gallery`, { headers: authHeaders() });
  if (!res.ok) return { ok: false, status: res.status };
  const json = await res.json();
  return {
    ok: true,
    purchases: json.purchases || [],
    searches: json.searches || [],
  };
}

export async function claimPurchases(): Promise<number> {
  const res = await fetch(`${API_URL}/users/me/claim-purchases`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) return 0;
  const json = await res.json();
  return json.claimed ?? 0;
}

export async function saveSearch(
  school: string,
  sessionDate: string,
  matchFilenames: string[],
  statistics?: Record<string, unknown>
): Promise<boolean> {
  const res = await fetch(`${API_URL}/users/me/searches`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      school,
      session_date: sessionDate,
      match_filenames: matchFilenames,
      statistics,
    }),
  });
  return res.ok;
}

export function meDownloadFileUrl(purchaseItemId: string): string {
  return `${API_URL}/users/me/downloads/file?purchase_item_id=${encodeURIComponent(purchaseItemId)}&token=${encodeURIComponent(getAccessToken() || '')}`;
}

export async function downloadMeFile(purchaseItemId: string): Promise<void> {
  const token = getAccessToken();
  const url = `${API_URL}/users/me/downloads/file?purchase_item_id=${encodeURIComponent(purchaseItemId)}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Descarga no permitida');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  let filename = 'photo.jpg';
  const m = cd?.match(/filename="?([^";]+)"?/);
  if (m) filename = m[1];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function parseFilenameFromDisposition(cd: string | null, fallback: string): string {
  if (!cd) return fallback;
  const m = cd.match(/filename\*?=(?:UTF-8''|"?)([^";]+)"?/i);
  return m ? decodeURIComponent(m[1]) : fallback;
}

function triggerBrowserDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Descarga validando la respuesta sin bufferizar el archivo entero en memoria.
 * Para ZIPs grandes, el navegador inicia la descarga al instante (streaming).
 */
export async function downloadUrlAsFile(url: string, fallbackFilename = 'descarga'): Promise<void> {
  try {
    const head = await fetch(url, { method: 'HEAD' });
    if (head.ok) {
      const filename = parseFilenameFromDisposition(
        head.headers.get('Content-Disposition'),
        fallbackFilename,
      );
      triggerBrowserDownload(url, filename);
      return;
    }
    if (head.status === 405 || head.status === 501) {
      triggerBrowserDownload(url, fallbackFilename);
      return;
    }
    const errRes = await fetch(url);
    let detail = '';
    try {
      const body = await errRes.json();
      detail = typeof body?.detail === 'string' ? body.detail : '';
    } catch {
      // respuesta no JSON
    }
    throw new Error(detail || `No se pudo descargar el archivo (HTTP ${head.status})`);
  } catch (err) {
    if (err instanceof Error && err.message && !err.message.includes('Failed to fetch')) {
      throw err;
    }
    // HEAD puede fallar en algunos navegadores; intentar descarga directa.
    triggerBrowserDownload(url, fallbackFilename);
  }
}

export function guestDownloadFileUrl(
  sessionId: string,
  school: string,
  date: string,
  filename: string
): string {
  const q = new URLSearchParams({
    session_id: sessionId,
    school,
    date: date || '',
    filename,
  });
  return `${API_URL}/users/guest/downloads/file?${q}`;
}

export function guestDownloadZipUrl(sessionId: string): string {
  return `${API_URL}/users/guest/downloads/zip?session_id=${encodeURIComponent(sessionId)}`;
}

export function meDownloadZipUrl(purchaseId: string): string {
  const token = getAccessToken();
  return `${API_URL}/users/me/downloads/zip/${encodeURIComponent(purchaseId)}?authorization=Bearer%20${encodeURIComponent(token || '')}`;
}

export async function markGuestDownloaded(sessionId: string): Promise<void> {
  await fetch(`${API_URL}/users/guest/mark-downloaded`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });
}

export function previewUrl(school: string, filename: string, date?: string, watermark = true): string {
  const q = new URLSearchParams({
    folder_name: school,
    filename,
    watermark: String(watermark),
  });
  if (date) q.set('day', date);
  return `${API_URL}/photos/preview?${q}`;
}

export function securePreviewUrl(
  school: string,
  filename: string,
  date: string,
  sessionId: string,
  dlToken: string
): string {
  const q = new URLSearchParams({
    folder_name: school,
    filename,
    watermark: 'false',
    day: date || '',
    dl_token: dlToken,
    session_id: sessionId,
  });
  return `${API_URL}/photos/preview?${q}`;
}
